import {computed, ref} from 'vue'
import {defineStore} from 'pinia'
import {useLocalStream, useSound} from '@/composables'
import {deviceTypesMap, getLocalStorage} from '@browser'
import {UAParser} from 'ua-parser-js'
import {mediator, messenger} from '@communication'
import {useAppStore, useSessionStore} from '@/stores/index'

const appInitialState = window.__INITIAL_STATE__?.app

export default defineStore('user', () => {
  const id = ref(null)
  const email = ref(null)
  const fullName = ref(null)
  const party = ref(null)
  const roles = ref([])
  const qualityNetwork = ref('average')
  const location = ref(null)
  const ua = UAParser()
  const status = 'online'
  const totalWebrtcByte = ref(0)
  const totalWsByte = ref(0)
  const {playSound} = useSound(['confirm-up', 'confirm-down'])
  const storage = getLocalStorage('devices')
  const dataLimit = ref(storage?.dataLimit || 0)
  const unitData = ref(storage?.unitData || 'MB')
  const profile = ref({
    ...(appInitialState.isMicrophoneRecommended ? {microphone: 'not-available'} : {}),
    ...(appInitialState.isCameraRecommended ? {camera: 'not-available'} : {}),
    os: ua.os,
    presence: document.visibilityState === 'visible',
    device: {
      ...ua.device,
      ...(!ua.device.type && {type: 'desktop'}),
    },
    browser: ua.browser,
    cpu: ua.cpu,
    engine: ua.engine,
    viewport: {
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
    },
    signal: null,
    qualityNetwork: qualityNetwork,
  })
  const networkSpeed = ref({
    uploadSpeed: 0,
    downloadSpeed: 0,
  })
  const hasCameraAvailable = computed(() => profile.value.camera !== 'not-available')
  const hasCameraMuted = computed(() => profile.value.camera === 'muted')
  const hasMicrophoneAvailable = computed(() => profile.value.microphone !== 'not-available')
  const hasMicrophoneMuted = computed(() => profile.value.microphone === 'muted')

  const isClient = computed(() => party.value === 'client')
  const isAssistant = computed(() => party.value === 'assistant')
  const isObserver = computed(() => party.value === 'observer')
  const consumptionData = computed(() => ((totalWebrtcByte.value + totalWsByte.value) / 1024 ** (unitData.value === 'KB' ? 1 : unitData.value === 'MB' ? 2 : 3)).toFixed(2))
  const app = useAppStore()
  const session = useSessionStore()
  const members = computed(() =>
    Object.values(
      [...session.info.participants, ...session.users].reduce((acc, obj) => {
        acc[obj.id] = obj
        return acc
      }, {})
    )
  )

  const isInLobby = computed(() => {
    return app.state.matches('app.view.meeting.lobby')
  })

  const wifiMap = new Map([
    ['good', {name: 'good-wifi', color: 'green'}],
    ['average', {name: 'average-wifi', color: '#979729'}],
    ['poor', {name: 'poor-wifi', color: 'red'}],
    ['offline', {name: 'wifi-off', color: 'black'}],
  ])

  const networkIcon = quality => wifiMap.get(quality) || {name: 'average-wifi', color: 'grey'}

  function setUser(id, email, fullName, party) {
    const roles = appInitialState?.roles?.[party] || []
    this.$patch({
      id,
      email,
      fullName,
      party,
      roles,
    })
  }

  function setLocation(location) {
    this.$patch({
      location,
    })
  }

  function setViewport(width, height) {
    this.$patch({
      profile: {
        viewport: {
          width,
          height,
        },
      },
    })
    this.profileChanged()
  }

  function setPresence(presence) {
    this.$patch({
      profile: {
        presence,
      },
    })
    this.profileChanged()
  }

  function setTorchCapability(hasTorchCapability) {
    this.$patch({
      profile: {
        hasTorchCapability: hasTorchCapability,
      },
    })
    this.profileChanged()
  }

  function setTorchStatus(status) {
    this.$patch({
      profile: {
        torchStatus: status,
      },
    })
    this.profileChanged()
  }

  function setSignal(signal) {
    this.$patch({
      profile: {
        signal,
      },
    })
    this.profileChanged()
  }

  function mute(type, isSilent = false) {
    const device = deviceTypesMap.get(type)
    if (profile.value[device] !== 'not-available') {
      const {mute} = useLocalStream()
      this.$patch({
        profile: {[device]: 'muted'},
      })
      this.profileChanged()
      mute(type)
      if (!isSilent) {
        playSound('confirm-down')
      }
    }
  }

  function setMuted(type) {
    const device = deviceTypesMap.get(type)
    if (profile.value[device] !== 'not-available') {
      this.$patch({
        profile: {[device]: 'muted'},
      })
      this.profileChanged()
    }
  }

  function unmute(type) {
    const device = deviceTypesMap.get(type)
    if (profile.value[device] !== 'not-available') {
      const {unmute} = useLocalStream()
      this.$patch({
        profile: {[device]: 'on'},
      })
      this.profileChanged()
      unmute(type)
      playSound('confirm-up')
    }
  }

  function disjoin() {
    mediator.session
      .disjoin()
      .then(() => {
        // TODO
      })
      .catch(error => {
        // TODO
        console.warn('disjoin error', error)
      })
  }

  function can(role) {
    return roles.value.includes(role)
  }

  function changeRole(role) {
    party.value = role
    roles.value = appInitialState?.roles?.[role] || []
  }

  function setQualityNetwork(qualityNetwork) {
    this.$patch({
      qualityNetwork,
    })
    this.$patch({
      profile: {
        qualityNetwork: qualityNetwork,
      },
    })
    this.profileChanged()
  }

  function setNetworkSpeed(networkSpeed) {
    this.$patch({
      networkSpeed,
    })
  }

  function _getSize(data) {
    if (data instanceof Blob) {
      return data.size
    } else if (data instanceof ArrayBuffer) {
      return data.byteLength
    } else {
      return new TextEncoder().encode(data).length
    }
  }
  setInterval(async () => {
    const totalBits = (await mediator.connection?.getTotalBits()) || 0;
    totalWebrtcByte.value = Math.round(totalBits / 8);
  }, Number(import.meta.env.VITE_DURATION_OF_NETWORK_QUALITY_MONITORING))

  messenger.subscribe('store', 'set-total-webrtc-byte', (size) => {
    totalWebrtcByte.value = size;
  });

  messenger.subscribe('store', 'increase-ws-byte', (size) => {
    const add = typeof size === 'number' ? size : _getSize(size);
    totalWsByte.value += add;
  });

  function setDataLimit(dataLimit) {
    this.$patch({
      dataLimit,
    })
  }

  function setUnitData(unitData) {
    this.$patch({
      unitData,
    })
  }

  function profileChanged() {
  }

  return {
    networkIcon,
    isInLobby,
    members,
    email,
    fullName,
    id,
    party,
    location,
    ua,
    profile,
    roles,
    status,
    networkSpeed,
    qualityNetwork,
    dataLimit,
    unitData,
    consumptionData,

    hasMicrophoneAvailable,
    hasMicrophoneMuted,
    hasCameraAvailable,
    hasCameraMuted,
    isClient,
    isAssistant,
    isObserver,
    setUser,
    setLocation,
    setViewport,
    setPresence,
    setSignal,
    mute,
    setMuted,
    unmute,
    disjoin,
    can,
    changeRole,
    profileChanged,
    setTorchCapability,
    setTorchStatus,
    setQualityNetwork,
    setNetworkSpeed,
    setDataLimit,
    setUnitData,
  }
})
